import { MetricDefinitionPart } from "@redotech/redo-model/checkouts/metric-definition";
import { assertNever } from "@redotech/util/type";
import { Link } from "react-router-dom";
import { Flex } from "../flex";
import { ExternalLink } from "../link";
import { Text } from "../text";
import * as css from "./metric-tooltip.module.css";
import { Tooltip } from "./tooltip";

interface MetricDefinition {
  lhs: string;
  rhs: readonly MetricDefinitionPart[];
}

export interface RedoMetricTooltipProps {
  title?: string | undefined;
  metricDescription?: string | string[] | undefined;
  metricDefinition?: MetricDefinition | undefined;
  metricEvaluation?: MetricDefinition | undefined;
  externalLink?:
    | { text: string; url: string; isInternal?: boolean }
    | undefined;
}

function MetricFormula({ formula }: { formula: MetricDefinition }) {
  return (
    <Flex as="span" display="inline" pt="2xl">
      <Text
        as="span"
        fontFamily="monospace"
        fontSize="xs"
        fontWeight="medium"
        textColor="tooltip-supporting-text"
      >
        {formula.lhs}
      </Text>{" "}
      ={" "}
      {formula.rhs.map((part, index) => {
        switch (part.type) {
          case "operator":
            return (
              <Text
                as="span"
                fontFamily="monospace"
                fontSize="xs"
                fontWeight="medium"
                key={index}
                textColor="tooltip-supporting-text"
              >{` ${part.operator} `}</Text>
            );
          case "left-parenthesis":
            return (
              <Text
                as="span"
                fontFamily="monospace"
                fontSize="xs"
                fontWeight="medium"
                key={index}
                textColor="tooltip-supporting-text"
              >
                {"( "}
              </Text>
            );
          case "right-parenthesis":
            return (
              <Text
                as="span"
                fontFamily="monospace"
                fontSize="xs"
                fontWeight="medium"
                key={index}
                textColor="tooltip-supporting-text"
              >
                {" )"}
              </Text>
            );
          case "term":
            return (
              <Text
                as="span"
                fontFamily="monospace"
                fontSize="xs"
                fontWeight="medium"
                key={index}
                textColor="tooltip-calculation-text"
              >
                {part.value}
              </Text>
            );
          default:
            assertNever(part);
        }
      })}
    </Flex>
  );
}

function MetricDescription({
  description,
}: {
  description: string | string[];
}) {
  if (Array.isArray(description)) {
    return description.map((line, index) => (
      <Text
        fontSize="xs"
        fontWeight="medium"
        key={index}
        textColor="tooltip-supporting-text"
      >
        {line}
      </Text>
    ));
  }
  return (
    <Text fontSize="xs" fontWeight="medium" textColor="tooltip-supporting-text">
      {description}
    </Text>
  );
}

function MetricTitle({ title }: { title: string }) {
  return (
    <Flex pb="xs">
      <Text fontSize="xs" fontWeight="semibold" textColor="white">
        {title}
      </Text>
    </Flex>
  );
}

function MetricLink({
  link,
}: {
  link: { text: string; url: string; isInternal?: boolean };
}) {
  return (
    <Flex pt="2xl">
      {link.isInternal ? (
        <Link className={css.externalLink} to={link.url}>
          <span className={css.internalLinkLabel}>{link.text}</span>
        </Link>
      ) : (
        <ExternalLink
          className={css.externalLink}
          showIcon={false}
          url={link.url}
        >
          {link.text}
        </ExternalLink>
      )}
    </Flex>
  );
}

export const RedoMetricTooltip = function RedoMetricTooltip({
  title,
  metricDescription,
  metricDefinition,
  metricEvaluation,
  externalLink,
  children,
}: { children: React.ReactElement<any, any> } & RedoMetricTooltipProps) {
  const content = (
    <Flex dir="column">
      {title && <MetricTitle title={title} />}
      {metricDescription && (
        <MetricDescription description={metricDescription} />
      )}
      {metricDefinition && <MetricFormula formula={metricDefinition} />}
      {metricEvaluation && <MetricFormula formula={metricEvaluation} />}
      {externalLink && <MetricLink link={externalLink} />}
    </Flex>
  );

  return (
    <Tooltip arrow leftAlign paddingOverride="12px 12px" title={content}>
      {children}
    </Tooltip>
  );
};
